/**
 * Preset icon library (widget customization v2) — shared by the dashboard picker
 * and the widget runtime so both draw the *same* glyph for a given id.
 *
 * Each entry is 24×24 viewBox path data using `currentColor`, so the consumer
 * controls the color via CSS `color`. The dashboard renders these in the badge /
 * avatar pickers; the widget renders the selected id on the launcher + avatars.
 *
 * Ids are stable strings persisted in `OrgTheme` (`badgeStyle`, `agentIcon`,
 * `userIcon`). `'custom'` (upload) and `'none'` are handled by the consumer, not
 * here. Unknown ids fall back to `DEFAULT_LAUNCHER_ICON` / a neutral avatar.
 */

/** A preset icon: an id, a human label (for the picker), and 24×24 SVG path markup. */
export interface PresetIcon {
  id: string;
  label: string;
  /** Inner SVG markup (paths/circles) using `currentColor`; drawn in a 0 0 24 24 viewBox. */
  svg: string;
}

/** Launcher badge presets (the floating button glyph). */
export const LAUNCHER_ICONS: PresetIcon[] = [
  {
    id: 'chat',
    label: 'Chat bubble',
    svg: '<path fill="currentColor" d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4V6a2 2 0 0 1 2-2Z"/>',
  },
  {
    id: 'message',
    label: 'Message dots',
    svg: '<path fill="currentColor" d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4V6a2 2 0 0 1 2-2Z"/><circle cx="8.5" cy="10.5" r="1.3" fill="#fff"/><circle cx="12" cy="10.5" r="1.3" fill="#fff"/><circle cx="15.5" cy="10.5" r="1.3" fill="#fff"/>',
  },
  {
    id: 'robot',
    label: 'Robot',
    svg: '<rect x="4" y="7" width="16" height="12" rx="3" fill="currentColor"/><circle cx="9" cy="13" r="1.6" fill="#fff"/><circle cx="15" cy="13" r="1.6" fill="#fff"/><rect x="9" y="16" width="6" height="1.6" rx="0.8" fill="#fff"/><rect x="11" y="3" width="2" height="3" rx="1" fill="currentColor"/><circle cx="12" cy="3" r="1.4" fill="currentColor"/>',
  },
  {
    id: 'headset',
    label: 'Support headset',
    svg: '<path fill="currentColor" d="M12 3a8 8 0 0 0-8 8v5a3 3 0 0 0 3 3h1v-7H6v-1a6 6 0 0 1 12 0v1h-2v7h1a3 3 0 0 0 3-3v-5a8 8 0 0 0-8-8Z"/>',
  },
  {
    id: 'help',
    label: 'Question mark',
    svg: '<circle cx="12" cy="12" r="9" fill="currentColor"/><path d="M12 7.5a2.5 2.5 0 0 0-2.5 2.5h1.8a.7.7 0 1 1 1.2.5c-.4.4-1.4.9-1.4 2v.4h1.7v-.2c0-.6 1.1-1 1.1-2.3A2.4 2.4 0 0 0 12 7.5Z" fill="#fff"/><circle cx="12" cy="16" r="1" fill="#fff"/>',
  },
  {
    id: 'wave',
    label: 'Waving hand',
    svg: '<path fill="currentColor" d="M7 11 5.7 9.7a1 1 0 0 1 1.4-1.4l1 1V6.2a1 1 0 0 1 2 0v2.3l.6-3a1 1 0 0 1 2 .4l-.4 2.4.9-2.2a1 1 0 0 1 1.9.7l-.9 2.3 1.1-1.1a1 1 0 0 1 1.4 1.4l-2.3 3.3a5 5 0 0 1-9.9-1.2Z"/>',
  },
];

/** Avatar presets (agent + visitor). A subset overlaps the launcher glyphs. */
export const AVATAR_ICONS: PresetIcon[] = [
  {
    id: 'agent',
    label: 'Support agent',
    svg: '<circle cx="12" cy="9" r="4" fill="currentColor"/><path fill="currentColor" d="M4 20a8 8 0 0 1 16 0Z"/>',
  },
  {
    id: 'headset',
    label: 'Headset agent',
    svg: '<path fill="currentColor" d="M12 3a7 7 0 0 0-7 7v4a2 2 0 0 0 2 2h1v-5H7v-1a5 5 0 0 1 10 0v1h-1v5h1a2 2 0 0 0 2-2v-4a7 7 0 0 0-7-7Z"/><path fill="currentColor" d="M9 17h6a3 3 0 0 1-6 0Z"/>',
  },
  {
    id: 'robot',
    label: 'Bot',
    svg: '<rect x="4" y="7" width="16" height="12" rx="3" fill="currentColor"/><circle cx="9" cy="13" r="1.6" fill="#fff"/><circle cx="15" cy="13" r="1.6" fill="#fff"/><rect x="9" y="16" width="6" height="1.6" rx="0.8" fill="#fff"/>',
  },
  {
    id: 'person',
    label: 'Person',
    svg: '<circle cx="12" cy="8" r="4" fill="currentColor"/><path fill="currentColor" d="M4 21a8 8 0 0 1 16 0Z"/>',
  },
  {
    id: 'star',
    label: 'Star',
    svg: '<path fill="currentColor" d="m12 3 2.7 5.5 6 .9-4.3 4.2 1 6-5.4-2.8-5.4 2.8 1-6L3.3 9.4l6-.9Z"/>',
  },
];

/** Default launcher glyph id when none/unknown is set. */
export const DEFAULT_LAUNCHER_ICON = 'chat';
/** Default agent avatar glyph id. */
export const DEFAULT_AGENT_ICON = 'agent';
/** Default visitor avatar glyph id. */
export const DEFAULT_USER_ICON = 'person';

const _launcherById = new Map(LAUNCHER_ICONS.map((i) => [i.id, i]));
const _avatarById = new Map(AVATAR_ICONS.map((i) => [i.id, i]));

/** Look up a launcher preset by id (falls back to the default chat bubble). */
export function launcherIcon(id: string | undefined): PresetIcon {
  return _launcherById.get(id ?? '') ?? _launcherById.get(DEFAULT_LAUNCHER_ICON)!;
}

/** Look up an avatar preset by id, choosing the agent/user default when unknown. */
export function avatarIcon(id: string | undefined, side: 'agent' | 'user'): PresetIcon {
  const fallback = side === 'agent' ? DEFAULT_AGENT_ICON : DEFAULT_USER_ICON;
  return _avatarById.get(id ?? '') ?? _avatarById.get(fallback)!;
}

/** Wrap a preset's inner markup in a full `<svg>` string (for `innerHTML` / `dangerouslySetInnerHTML`). */
export function presetSvg(icon: PresetIcon, size = 24): string {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" aria-hidden="true" focusable="false">${icon.svg}</svg>`;
}
